import {StarRating, StarRatingProps} from '@udemy-v2/react-merchandising-components';
import {useI18n} from '@udemy-v2/i18n';
import {TrackImpression} from '@udemy-v2/event-tracking';
import {Tooltip} from '@udemy-v2/react-popup-components';
import classNames from 'classnames';
import React, {useMemo} from 'react';

import {ReviewSummaryRatingGauge} from './review-summary-rating-gauge.react-component';
import styles from './review-summary-widget.module.scss';

const noop = () => {};

interface ReviewSummaryWidgetProps {
    isFreeSEOExp?: boolean;
    showAverageRating?: boolean;
    averageRating: number;
    totalDistributionCount: number;
    ratingDistribution: {rating: number; count: number}[];
    selectedRating?: number;
    onRatingSelected?: (rating: number | undefined) => void;
    onSeen?: () => void;
    ratingText?: string;
    ratingSize?: StarRatingProps['size'];
    averageContainerProps?: React.HTMLAttributes<HTMLDivElement>;
}

export const ReviewSummaryWidget = ({
    isFreeSEOExp = false,
    showAverageRating = false,
    averageRating,
    totalDistributionCount,
    ratingDistribution,
    selectedRating,
    onRatingSelected,
    onSeen = noop,
    ratingText,
    ratingSize = 'large',
    averageContainerProps = {},
}: ReviewSummaryWidgetProps) => {
    const {gettext, interpolate} = useI18n();

    const ratingDistributionWithPercentage = useMemo(() => {
        const distribution = ratingDistribution.map((range) => ({
            ...range,
            percentage:
                totalDistributionCount > 0 ? (range.count / totalDistributionCount) * 100 : 0,
        }));

        const totalPercentage = distribution.reduce(
            (total, range) => total + Math.round(range.percentage),
            0,
        );

        if (totalPercentage !== 100 && distribution.length > 0) {
            let highestRangeIndex = 0;
            let highestPercentage = 0;
            distribution.forEach((range, index) => {
                if (range.percentage > highestPercentage) {
                    highestPercentage = range.percentage;
                    highestRangeIndex = index;
                }
            });
            distribution[highestRangeIndex].percentage += 100 - totalPercentage;
        }
        return distribution;
    }, [ratingDistribution, totalDistributionCount]);

    const getAriaTextForRating = (rating: number) => {
        return interpolate(gettext('Rating: %(rating)s out of 5'), {rating}, true);
    };

    if (averageRating === 0) {
        return (
            <div data-purpose="no-ratings-message">
                {gettext("This course doesn't have any reviews yet.")}
            </div>
        );
    }

    const renderFilterApplied = (filter?: number) => {
        const filterApplied = `${filter} star filter applied.`;
        return <span>{filter ? filterApplied : 'No filter applied'}</span>;
    };

    return (
        <TrackImpression trackFunc={onSeen}>
            <div data-purpose="ratings-and-reviews">
                <div role={'status'} className="ud-sr-only">
                    {renderFilterApplied(selectedRating)}
                </div>
                <div className={styles['stats-container']}>
                    {showAverageRating && (
                        <div
                            {...averageContainerProps}
                            className={classNames(
                                'ud-text-md',
                                styles['average-container'],
                                averageContainerProps.className,
                            )}
                        >
                            <div
                                data-purpose="average-rating"
                                className={classNames('ud-heading-xxl', styles['average-number'])}
                            >
                                {averageRating.toFixed(1)}
                            </div>

                            <div className={styles['average-stars']}>
                                <StarRating
                                    data-purpose="star-rating-average"
                                    ariaLabel={getAriaTextForRating(averageRating)}
                                    rating={averageRating}
                                    size={ratingSize}
                                />
                            </div>

                            <div
                                className={classNames(
                                    'ud-heading-lg',
                                    styles['average-rating-text'],
                                )}
                            >
                                <Tooltip
                                    placement="bottom"
                                    renderTrigger={(triggerProps) => (
                                        <span {...triggerProps}>
                                            {ratingText ??
                                                (isFreeSEOExp
                                                    ? gettext('Tutorial rating')
                                                    : gettext('Course Rating'))}
                                        </span>
                                    )}
                                >
                                    {gettext(
                                        `Course Ratings are calculated from individual students' ratings 
                                            and a variety of other signals, like age of rating and reliability, to ensure 
                                            that they reflect course quality fairly and accurately.`,
                                    )}
                                </Tooltip>
                            </div>
                        </div>
                    )}
                    <div
                        className={classNames(styles.rates, {
                            [styles['with-average-rating']]: showAverageRating,
                        })}
                    >
                        {ratingDistributionWithPercentage
                            .map((range, index) => (
                                <ReviewSummaryRatingGauge
                                    key={`rating-${range.rating}-${index}`}
                                    ariaLabel={getAriaTextForRating(range.rating)}
                                    selectedRating={selectedRating}
                                    rating={range.rating}
                                    percent={range.percentage}
                                    size={ratingSize}
                                    onRatingSelected={onRatingSelected}
                                />
                            ))
                            .reverse()}
                    </div>
                </div>
            </div>
        </TrackImpression>
    );
};
